/* eslint-disable arrow-body-style, object-curly-newline,no-nested-ternary */
// eslint-disable-next-line import/no-extraneous-dependencies
import { useCallback, useEffect, useRef, useState } from 'react'
import { FormGroup, FormHelperText, FormLabel, Grid, Input } from '@mui/material'
import Prism from 'prismjs'
import DOMPurify from 'dompurify'
import { classNames, PlatformEvent } from 'lib/util'
import useMemoRef from '@platform/react/hook/useMemoRef'
import { useStyles } from 'platform/react/hook'

Prism.manual = true
Prism.highlightAll()

/**
 * Returns the {@link CodeField} element's theme styles.
 *
 * @param {Object} theme                the application's current theme
 * @param {Object} props                component's current props
 * @param {boolean} [props.fullHeight]  whether the component should take the parent's whole height
 * @param {number} [props.minRows]      the minimum number of text lines taken by the element
 * @param {number} [props.maxRows]      the maximum number of text lines taken by the element
 * @param {number} [props.rows]         the current number of text lines taken by the element
 * @returns {Object}                    the CodeField element's styles
 */
const styles = (theme, { fullHeight, minRows, maxRows, rows }) => {
  const lineHeight = '1.2rem'
  const minHeight = '2.5rem'
  const currentRows = rows > minRows ? rows > maxRows ? maxRows : rows : minRows
  const containerHeight = currentRows > 1 ? `calc(${currentRows} * ${lineHeight})` : minHeight
  const containerMaxHeight = currentRows > 1 ? `calc(${maxRows || 1} * ${lineHeight})` : minHeight
  const inputHeight = currentRows > 1 ? `calc(${currentRows} * ${lineHeight} - 9px)` : minHeight
  const inputMaxHeight = currentRows > 1 ? `calc(${maxRows || 1} * ${lineHeight} - 9px)` : minHeight

  return {
    root: {
      flexDirection: 'column',
      flexWrap: 'nowrap',
      ...fullHeight && {
        height: ['100%', '!important'],
      },
    },
    label: {
      color: '#7D7E80',
      transformOrigin: 'left',
      transform: 'translate(0, 1.75rem)',
      transition: [
        ['transform 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms'],
        ['color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms'],
      ],
      '&.MuiFormLabel-filled': {
        transform: 'translate(0, -1.5px) scale(0.75)',
      },
    },
    container: {
      position: 'relative',
      ...fullHeight ? {
        height: ['100%', '!important'],
      } : {
        height: [[containerHeight], '!important'],
        minHeight: containerHeight,
        maxHeight: containerMaxHeight,
      },
    },
    textLayout: {
      fontSize: 14,
      fontFamily: 'monospace',
      lineHeight,
      tabSize: 2,
    },
    fill: {
      height: [['100%'], '!important'],
      width: '100%',
    },
    absoluteFill: {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
    scrollable: {
      overflowY: ['auto', '!important'],
      scrollbarWidth: 'thin',
      scrollbarColor: [['#DDD', 'transparent']],
    },
    inputContainer: {
      display: 'flex',
      alignItems: 'flex-start',
      marginTop: [[0], '!important'],
      '& textarea': {
        overflowY: ['scroll', '!important'],
        scrollbarWidth: 'thin',
        scrollbarColor: [['#DDD', 'transparent']],
        ...fullHeight ? {
          minHeight: '100%',
          maxHeight: '100%',
        } : {
          paddingBottom: 5,
          height: [[inputHeight], '!important'],
          minHeight: [[inputHeight], '!important'],
          maxHeight: inputMaxHeight,
        },
      },
    },
    input: {
      zIndex: 1,
      color: 'transparent',
      background: 'transparent',
      caretColor: 'black',
      alignItems: 'flex-start',
    },
    codeContainer: {
      marginTop: 0,
      paddingTop: 4,
      paddingBottom: 5,
    },
    code: {
      zIndex: 0,
      display: 'block',
      textWrap: 'wrap',
    },
    helperText: {
      marginTop: 3,
    },
  }
}

/**
 * Uses {@link Prism} to highlight `value` syntax belonging to `language`.
 *
 * @param {string} value    value to highlight
 * @param {string} language programming language
 * @returns {string}         HTML result of highlighting `value`
 */
const highlight = (value, language) => {
  return Prism.highlight(value, Prism.languages[language], language)
}

/**
 * CodeField Element
 *
 * Displays a text field whose content is highlighted according to the syntax of a specified
 * programming language.
 *
 * @param {Object} props                      element properties
 * @param {React.Ref} props.forwardedRef      ref to the root element
 * @param {string} [props.label]              field label
 * @param {string} props.language             programming language whose syntax to consider
 * @param {Object} [props.events]             events for the radio field
 * @param {function} [props.events.onChange]  triggered whenever the field's value changes
 * @param {string} [props.helperText]         instructive text displayed below the field
 * @param {number} [props.minRows]            the minimum number of text lines taken by the element
 * @param {number} [props.maxRows]            the maximum number of text lines taken by the element
 * @param {boolean} [props.fullHeight]        whether the field should use the parent's whole height
 * @param {string|null} [props.value]         field current's value
 * @param {boolean} [props.error]             whether the field is in error state
 * @returns {React.ReactNode}
 */
const CodeField = ({
  forwardedRef,
  label,
  language,
  events,
  helperText,
  minRows,
  maxRows,
  fullHeight,
  ...props
}) => {
  const [value, setValue] = useState(props.value || '')
  const [code, setCode] = useState(highlight(props.value || '', language))
  const [rows, setRows] = useState(minRows)
  const [cursor, setCursor] = useState(0)
  const [focused, setFocused] = useState(false)

  const classes = useStyles(styles, { fullHeight, minRows, maxRows, rows })()

  const codeRef = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    const input = inputRef.current
    input.selectionStart = cursor
    input.selectionEnd = cursor
  }, [cursor])

  useEffect(() => {
    setCode(highlight(value, language))
    setRows((value?.match(/\n/g)?.length || 0) + 1)
  }, [value, language])

  useEffect(() => {
    setValue(props.value || '')
  }, [props.value])

  const handleTab = useCallback((event) => {
    if (event.key === 'Tab') {
      event.preventDefault()
      const input = inputRef.current
      const inputValue = input.value

      const beforeTab = inputValue.slice(0, input.selectionStart)
      const afterTab = inputValue.slice(input.selectionEnd, inputValue.length)
      const newValue = `${beforeTab}\t${afterTab}`

      setValue(newValue)
      events?.onChange(new PlatformEvent(event, { value: newValue }))
      setCursor(input.selectionStart + 1)
    }
  }, [])

  const handleScroll = useCallback(() => {
    const codeElement = codeRef.current
    const inputElement = inputRef.current

    codeElement.scrollTop = inputElement.scrollTop
    codeElement.scrollLeft = inputElement.scrollLeft
  }, [])

  const handleChange = useCallback((event) => {
    const newValue = event.target.value

    setValue(newValue)
    events?.onChange(new PlatformEvent(event, { value: newValue }))
  }, [])

  return (
    <Grid
      item
      container
      component={FormGroup}
      className={classes.root}
    >
      <Grid
        item
        component={FormLabel}
        className={classes.label}
        htmlFor={'code-input'}
        filled={!!value || focused}
        xs={'auto'}
      >
        {label}
      </Grid>
      <Grid
        item
        container
        className={classes.container}
        flexShrink={1}
        xs={12}
      >
        <Grid
          item
          component={'pre'}
          className={classNames(
            classes.absoluteFill,
            classes.codeContainer,
          )}
        >
          <Grid
            ref={codeRef}
            item
            component={'code'}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(code),
            }}
            className={classNames(
              classes.fill,
              classes.scrollable,
              classes.textLayout,
              classes.code,
            )}
          />
        </Grid>
        <Input
          id={'code-input'}
          inputRef={inputRef}
          value={value}
          onChange={handleChange}
          className={classNames(
            classes.absoluteFill,
            classes.inputContainer,
            classes.textLayout,
            classes.input,
          )}
          inputProps={{
            onScroll: handleScroll,
            onKeyDown: handleTab,
            onFocus: () => setFocused(true),
            onBlur: () => setFocused(false),
          }}
          variant={'standard'}
          spellCheck={false}
          multiline={true}
          minRows={minRows || 1}
          maxRows={maxRows}
        />
      </Grid>
      <Grid
        item
        component={FormHelperText}
        className={classes.helperText}
        error={props.error}
        xs={'auto'}
      >
        {helperText || '\u00A0'}
      </Grid>
    </Grid>
  )
}

export default useMemoRef(CodeField, props => [
  props.value,
  props.label,
  props.language,
  props.helperText,
  props.error,
])
